<template>
  <div class="date-picker">
    <div class="date-picker-header">
      <button class="nav-button" @click="previousWeek">
        <img :src="arrowLeftIcon" alt="Previous" />
      </button>

      <span class="month-text">{{ currentMonthYear }}</span>

      <button class="today-button" @click="goToToday">Сегодня</button>

      <button class="nav-button" @click="nextWeek">
        <img :src="arrowRightIcon" alt="Next" />
      </button>
    </div>

    <div class="weekdays">
      <div
        class="weekday"
        v-for="(day, index) in weekdays"
        :key="day"
        :class="{ active: isWeekdayActive(index) }"
      >{{ day }}</div>
    </div>

    <div class="dates">
      <button
        v-for="date in weekDates"
        :key="date.dateString"
        class="date-button"
        :class="{
          active: isSelected(date.dateString),
          disabled: !date.hasSlots,
          'has-slots': date.hasSlots
        }"
        :disabled="!date.hasSlots"
        @click="selectDate(date.dateString)"
      >
        <span class="date-number">{{ date.day }}</span>
        <span
          v-if="date.hasSlots"
          class="slot-indicator"
          :style="{ '--slot-indicator-icon': `url(${eclipseIcon})` }"
          aria-hidden="true"
        ></span>
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, computed } from 'vue'
import eclipseIcon from '@/widgets/booking/assets/img/icons/booking-eclipse.svg'
import arrowRightIcon from "@/widgets/booking/assets/img/icons/arrow_right.svg";
import arrowLeftIcon from "@/widgets/booking/assets/img/icons/arrow_left.svg";

const props = defineProps({
  availableDates: {
    type: Array,
    required: true
  },
  modelValue: {
    type: String,
    default: undefined
  }
})

const emit = defineEmits(['update:modelValue'])

const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const currentWeekStart = ref(getMonday(new Date()))

const currentMonthYear = computed(() => {
  const date = new Date(currentWeekStart.value)
  const formatted = date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
  return formatted.replace(' г.', '')
})

const weekDates = computed(() => {
  const dates = []
  const start = new Date(currentWeekStart.value)

  for (let i = 0; i < 7; i++) {
    const date = new Date(start)
    date.setDate(start.getDate() + i)

    const dateString = formatDate(date)
    dates.push({
      dateString,
      day: date.getDate(),
      hasSlots: props.availableDates.includes(dateString)
    })
  }

  return dates
})

function getMonday(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function previousWeek() {
  const newDate = new Date(currentWeekStart.value)
  newDate.setDate(newDate.getDate() - 7)
  currentWeekStart.value = newDate
}

function nextWeek() {
  const newDate = new Date(currentWeekStart.value)
  newDate.setDate(newDate.getDate() + 7)
  currentWeekStart.value = newDate
}

function goToToday() {
  const today = new Date()
  const todayString = formatDate(today)

  // Прокручиваем к текущей неделе
  currentWeekStart.value = getMonday(today)

  // Выбираем сегодняшний день, если он доступен
  if (props.availableDates.includes(todayString)) {
    emit('update:modelValue', todayString)
  }
}

function isSelected(dateString) {
  return props.modelValue === dateString
}

function selectDate(dateString) {
  emit('update:modelValue', dateString)
}

function isWeekdayActive(weekdayIndex) {
  if (!props.modelValue) return false

  const selectedDate = new Date(props.modelValue)

  // Check if selected date is in current displayed week
  const weekStart = new Date(currentWeekStart.value)
  const weekEnd = new Date(currentWeekStart.value)
  weekEnd.setDate(weekEnd.getDate() + 6)

  // Set time to 0 for proper date comparison
  selectedDate.setHours(0, 0, 0, 0)
  weekStart.setHours(0, 0, 0, 0)
  weekEnd.setHours(23, 59, 59, 999)

  // If selected date is not in current week, return false
  if (selectedDate < weekStart || selectedDate > weekEnd) {
    return false
  }

  const dayOfWeek = selectedDate.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Convert to Monday-based index (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
  const mondayBasedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1

  return mondayBasedDay === weekdayIndex
}
</script>

<style scoped lang="scss">
@import '@/widgets/booking/assets/scss/variables.scss';

.date-picker {
  background: white;
  border-radius: 12px;
  margin-bottom: 24px;
}

.date-picker-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.nav-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 8px;
  color: var(--booking-widget-primary-color, #3C67D6);
  transition: all 0.2s ease;
  flex-shrink: 0;

  img {
    width: 12px;
    height: 12px;
    filter: var(--booking-widget-primary-icon-filter, none);
  }

  &:hover {
    background: darken(#F3F5F8, 5%);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.month-text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #333333;
  text-transform: capitalize;
  flex: 1;
}

.today-button {
  height: 24px;
  padding: 4px 8px;
  background: #F3F5F8;
  border-radius: 12px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: var(--booking-widget-primary-color, #3C67D6);
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: darken(#F3F5F8, 5%);
  }

  &:active {
    transform: scale(0.98);
  }
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 48px);
  margin-bottom: 8px;
  justify-content: space-between;
}

.weekday {
  text-align: center;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #9898A5;
  padding: 4px 0;
  transition: color 0.2s ease;

  &.active {
    color: var(--booking-widget-primary-color, #3C67D6);
  }
}

.dates {
  display: grid;
  grid-template-columns: repeat(7, 48px);
  justify-content: space-between;
}

.date-button {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 8px;
  gap: 4px;
  padding: 12px 6px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  color: #757885;
  transition: all 0.2s ease;

  &.has-slots {
    opacity: 1;
    cursor: pointer;

    &:hover {
      background: darken(#F3F5F8, 5%);
    }
  }

  &.active {
    background: var(--booking-widget-primary-color, #3C67D6);
    color: white;

    &:hover {
      background: var(--booking-widget-primary-color, #3C67D6);
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.date-number {
  line-height: 1;
}

.slot-indicator {
  width: 6px;
  height: 6px;
  background: var(--booking-widget-primary-color, #3C67D6);
  display: block;
  mask: var(--slot-indicator-icon) center / 4px 4px no-repeat;
  -webkit-mask: var(--slot-indicator-icon) center / 4px 4px no-repeat;
}
</style>
